import React, { useState } from "react";
import {
  addMonths,
  subMonths,
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isBefore,
  startOfDay,
  isAfter,
} from "date-fns";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useGlobalState } from "../../services/context/AuthContext";

interface CalendarProps {
  onDateSelect?: (date: Date) => void;
  calenderData: any[];
  fromDate: string; // Should be passed as "YYYY-MM-DD"
}

const Calendar: React.FC<CalendarProps> = ({
  onDateSelect,
  calenderData,
  fromDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(fromDate));
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const { userInfo } = useGlobalState();

  const fromDateObj = startOfDay(new Date(fromDate));
  const today = startOfDay(new Date());

  const minSelectableDate = today;

  const renderHeader = (monthOffset: number) => {
    const month = addMonths(currentMonth, monthOffset);
    return (
      <div className="flex justify-between items-center p-2 border-b calender-btn">
        {monthOffset === 0 && (
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 calender-btn ml-n4"
          >
            <IoIosArrowBack size={20} />
          </button>
        )}
        <h2 className="text-lg font-semibold text-center flex-1">
          {format(month, "MMMM yyyy")}
        </h2>
        {monthOffset === 1 && (
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2"
          >
            <IoIosArrowForward size={20} />
          </button>
        )}
      </div>
    );
  };

  const renderDays = () => {
    const days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    return (
      <div className="grid grid-cols-7 text-center font-semibold d-flex weekdays">
        {days.map((day, i) => (
          <div key={i} className="py-0 mx-2 px-2 py-sm-2 px-sm-3">
            <p>{day}</p>
          </div>
        ))}
      </div>
    );
  };

  const fareByDate: Record<string, number> = {};
  calenderData?.forEach((month: any) => {
    month?.lstCalFare?.forEach((fare: any) => {
      const dateStr = format(new Date(fare.DepDate), "yyyy-MM-dd");
      fareByDate[dateStr] = fare.TotalFare;
    });
  });

  const renderCells = (monthOffset: number) => {
    const monthStart = startOfMonth(addMonths(currentMonth, monthOffset));
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart); // alignment ke liye
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dayClone = new Date(day);
        const isCurrentMonth = isSameMonth(dayClone, monthStart);
        const isToday = isSameDay(dayClone, new Date());
        const isSelected = selectedDate && isSameDay(dayClone, selectedDate);
        const isHovered = hoveredDate && isSameDay(dayClone, hoveredDate);

        const dateKey = format(dayClone, "yyyy-MM-dd");
        const fareForDay = fareByDate[dateKey];
        const isDisabled = isBefore(startOfDay(dayClone), minSelectableDate);

        days.push(
          <div
            key={dateKey}
            className={`py-2 h-16 flex flex-col items-center justify-center transition-all date-div
             ${dayClone && isCurrentMonth ? "border" : ""}   
            ${isSelected ? "date-highlight" : ""}
            ${isHovered && !isSelected && !isToday ? "bg-gray-200" : ""}
            ${
              !isCurrentMonth
                ? "bg-gray-50 text-transparent cursor-default" // 👈 non-current month blank
                : isDisabled
                ? "cursor-not-allowed text-gray-400 opacity-60"
                : "hover:bg-gray-100 cursor-pointer"
            }
          `}
            onClick={() => {
              if (isCurrentMonth && !isDisabled) {
                setSelectedDate(dayClone);
                onDateSelect?.(dayClone);
              }
            }}
            onMouseEnter={() => {
              if (isCurrentMonth && !isDisabled) setHoveredDate(dayClone);
            }}
            onMouseLeave={() => setHoveredDate(null)}
          >
            {isCurrentMonth && <div>{format(dayClone, "d")}</div>}
            {isCurrentMonth && fareForDay &&   isAfter(dayClone, today) &&(
              <span className={`text-xs mt-1 text-danger`}>
                {userInfo?.currencyCode ?? "AED"} {fareForDay}
              </span>
            )}
          </div>
        );

        day = addDays(day, 1);
      }

      rows.push(
        <div className="h-14 flex items-center" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }

    return <div className="grid grid-cols-7 mt-2 mb-4">{rows}</div>;
  };

  return (
    <div className="calendar-container container">
      {/* Desktop View - Dual Calendar */}
      <div className="d-none d-md-flex bg-white shadow rounded p-2">
        <div className="calendar  -month m-2">
          {renderHeader(0)}
          {renderDays()}
          {renderCells(0)}
        </div>
        <div className="calendar-month m-2">
          {renderHeader(1)}
          {renderDays()}
          {renderCells(1)}
        </div>
      </div>

      {/* Mobile View - Single Calendar */}
      <div className="d-block d-md-none bg-white shadow rounded p-2">
        <div className="calendar-single-header d-flex justify-content-between align-items-center mb-2">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="btn btn-light"
          >
            <IoIosArrowBack size={20} />
          </button>
          <h2 className="text-center m-0">
            {format(currentMonth, "MMMM yyyy")}
          </h2>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="btn btn-light"
          >
            <IoIosArrowForward size={20} />
          </button>
        </div>
        {renderDays()}
        {renderCells(0)}
      </div>
    </div>
  );
};

export default Calendar;
